import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Input,
    Label,
    Nav,
    NavItem,
    NavLink,
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import { apiURL } from '../../config';
import Footer from "components/Footers/Footer";

const AddStudentAttendance = () => {
    const token = localStorage.getItem("token");
    const [selectedClass, setSelectedClass] = useState('');
    const [students, setStudents] = useState({});
    const [activeTab, setActiveTab] = useState('add');
    const [modalOpen, setModalOpen] = useState(false);
    const [attendanceDate, setAttendanceDate] = useState('');
    const [attendanceRecords, setAttendanceRecords] = useState({});
    const [previousAttendance, setPreviousAttendance] = useState({});
    const [filterDate, setFilterDate] = useState('');
    const [filterStudent, setFilterStudent] = useState('');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editDate, setEditDate] = useState('');
    const [editAttendanceRecords, setEditAttendanceRecords] = useState({});
    const [teacherId, setTeacherId] = useState('');
    const [isAttendanceMarking, setIsAttendanceMarking] = useState(false);
    const [editAttendanceData, setEditAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const searchInputRef = useRef(null);

    useEffect(() => {
        const user = localStorage.getItem('userInfo');
        if (user) {
            try {
                const parsedUser = JSON.parse(user);
                setTeacherId(parsedUser._id);
            } catch (error) {
                console.error("Error parsing user info from localStorage:", error);
                toast.error("Failed to load user info.");
            }
        }
    }, []);

    useEffect(() => {
        if (teacherId) {
            fetchStudents();
        }
    }, [teacherId]);

    useEffect(() => {
        const today = new Date();
        setFilterDate(formatDate(today));
    }, []);

    const fetchStudents = async () => {
        try {
            const response = await fetch(`${apiURL}/teacher/getStudents/${teacherId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            if (data && data.studentsByClass) {
                const transformedStudents = data.studentsByClass.reduce((acc, curr) => {
                    acc[curr.className] = curr.students;
                    return acc;
                }, {});
                setStudents(transformedStudents);
                if (Object.keys(transformedStudents).length > 0) {
                    setSelectedClass(Object.keys(transformedStudents)[0]);
                }
            } else {
                setStudents({});
                setSelectedClass('');
            }
        } catch (error) {
            console.error('Error fetching students:', error);
            toast.error('Error fetching students!');
            setStudents({});
            setSelectedClass('');
        }
    };

    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatTime = (date) => {
        if (!date) return '-';
        const d = new Date(date);
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const handleClassChange = (e) => {
        setSelectedClass(e.target.value);
        setIsAttendanceMarking(false);
        setAttendanceRecords({});
        setEditAttendanceRecords({});
        setEditAttendanceData([]);
    };

    const toggleTab = (tab) => {
        setActiveTab(tab);
        setIsAttendanceMarking(false);
        setAttendanceRecords({});
        setEditAttendanceRecords({});
        setEditAttendanceData([]);
    };

    const openModal = () => {
        setModalOpen(true);
        setAttendanceDate(formatDate(new Date()));
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    const handleAttendanceDateChange = (e) => {
        setAttendanceDate(e.target.value);
    };

    const handleMarkAttendance = async () => {
        if (!attendanceDate) {
            toast.warn('Please select a valid date.');
            return;
        }
        try {
            setLoading(true);
            const url = `${apiURL}/teacher/${teacherId}/get-prev-attendance?date=${attendanceDate}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            const allStudents = students[selectedClass] || [];
            const initialRecords = {};
            allStudents.forEach(student => {
                const attendance = data[selectedClass]?.find(att => att.studentId === student._id);
                initialRecords[student._id] = attendance?.attendance[0]?.status || 'Absent';
            });
            setAttendanceRecords(initialRecords);
            setPreviousAttendance(data);
            setIsAttendanceMarking(true);
            closeModal();
            // console.log('Initialized attendanceRecords:', initialRecords, 'Date:', attendanceDate);
        } catch (error) {
            console.error('Error fetching existing attendance for add:', error);
            toast.warn('No existing attendance found; initializing all as Absent.');
            const allStudents = students[selectedClass] || [];
            const initialRecords = {};
            allStudents.forEach(student => {
                initialRecords[student._id] = 'Absent';
            });
            setAttendanceRecords(initialRecords);
            setIsAttendanceMarking(true);
            closeModal();
        } finally {
            setLoading(false);
        }
    };

    const handleAttendanceChange = (studentId) => {
        setAttendanceRecords((prev) => {
            const newRecords = {
                ...prev,
                [studentId]: prev[studentId] === 'Present' ? 'Absent' : 'Present',
            };
            // console.log('Updated attendanceRecords:', newRecords);
            return newRecords;
        });
    };

    const submitAttendance = async () => {
        try {
            setLoading(true);
            const allStudents = students[selectedClass] || [];
            if (!attendanceDate || allStudents.length === 0) {
                toast.error('Date and student records are required.');
                setLoading(false);
                return;
            }
            const attendanceData = allStudents.map((student) => ({
                studentId: student?._id || '-',
                status: attendanceRecords[student._id] || 'Absent',
            }));

            // console.log('Submitting attendance:', { date: attendanceDate, attendanceRecords: attendanceData });

            const response = await fetch(`${apiURL}/teacher/add-student-attendance`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    date: attendanceDate,
                    attendanceRecords: attendanceData,
                }),
            });

            const responseData = await response.json();
            if (response.ok) {
                toast.success(responseData.message || 'Attendance submitted successfully!');
                setActiveTab('view');
                fetchPreviousAttendance();
                setIsAttendanceMarking(false);
                setAttendanceRecords({});
                setAttendanceDate('');
            } else {
                console.error('Submission failed:', responseData);
                const skipMessages = responseData.skipped?.map(s => `Student ${s.studentId}: ${s.reason}`).join('; ') || responseData.message;
                toast.error(skipMessages || 'Error submitting attendance!');
            }
        } catch (error) {
            console.error('Error submitting attendance:', error);
            toast.error(`Error submitting attendance: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const fetchPreviousAttendance = async () => {
        try {
            setLoading(true);
            const url = `${apiURL}/teacher/${teacherId}/get-prev-attendance?date=${filterDate}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            setPreviousAttendance(data);
            // console.log('Fetched previous attendance:', data);
        } catch (error) {
            // console.error('Error fetching previous attendance:', error);
            toast.error('Error fetching previous attendance!');
            setPreviousAttendance({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (teacherId && activeTab === 'view' && filterDate) {
            fetchPreviousAttendance();
        }
    }, [teacherId, activeTab, filterDate, selectedClass]);

    const handleFilterDateChange = (e) => {
        setFilterDate(e.target.value);
    };

    const handleFilterStudentChange = (e) => {
        setFilterStudent(e.target.value);
    };

    const openEditModal = () => {
        if (!filterDate) {
            toast.warn('Please select a date to edit attendance.');
            return;
        }
        setEditDate(filterDate);
        setEditModalOpen(true);
    };

    const handleEditDateSubmit = async () => {
        try {
            setLoading(true);
            const url = `${apiURL}/teacher/${teacherId}/get-prev-attendance?date=${editDate}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            if (response.ok && data && data[selectedClass] && data[selectedClass].length > 0) {
                setEditAttendanceData(data[selectedClass]);
                const initialEditRecords = {};
                data[selectedClass].forEach(student => {
                    // console.log(`Student ${student.studentId}: status=${student.attendance[0]?.status}, remarks=${student.attendance[0]?.remarks}`);
                    initialEditRecords[student.studentId] = student.attendance && student.attendance.length > 0 ? student.attendance[0].status : 'Absent';
                });
                setEditAttendanceRecords(initialEditRecords);
                setIsAttendanceMarking(true);
                setEditModalOpen(false);
                // console.log('Initialized editAttendanceRecords:', initialEditRecords);
            } else {
                setEditAttendanceData([]);
                toast.warn('No attendance records found for this date.');
                setEditModalOpen(false);
            }
        } catch (error) {
            console.error('Error fetching attendance data for edit:', error);
            toast.error(`Error fetching attendance data: ${error.message}`);
            setEditModalOpen(false);
        } finally {
            setLoading(false);
        }
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setEditDate('');
    };

    const handleEditDateChange = (e) => {
        setEditDate(e.target.value);
    };

    const submitEditAttendance = async () => {
        try {
            setLoading(true);
            const allStudents = students[selectedClass] || [];
            if (!editDate || allStudents.length === 0) {
                toast.error('Date and student records are required.');
                setLoading(false);
                return;
            }
            const attendanceData = allStudents.map((student) => ({
                studentId: student?._id || '-',
                status: editAttendanceRecords[student._id] || 'Absent',
            }));

            // console.log('Submitting edit attendance:', { date: editDate, attendanceRecords: attendanceData });

            const response = await fetch(`${apiURL}/teacher/edit-student-attendance`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    date: editDate,
                    attendanceRecords: attendanceData,
                }),
            });

            const responseData = await response.json();
            if (response.ok) {
                toast.success(responseData.message || 'Attendance updated successfully!');
                setIsAttendanceMarking(false);
                setEditAttendanceRecords({});
                setEditAttendanceData([]);
                fetchPreviousAttendance();
            } else {
                console.error('Edit submission failed:', responseData);
                const skipMessages = responseData.skipped?.map(s => `Student ${s.studentId}: ${s.reason}`).join('; ') || responseData.message;
                toast.error(skipMessages || 'Error updating attendance!');
            }
        } catch (error) {
            console.error('Error updating attendance:', error);
            toast.error(`Error updating attendance: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEditAttendanceChange = (studentId) => {
        setEditAttendanceRecords((prev) => {
            const newRecords = {
                ...prev,
                [studentId]: prev[studentId] === 'Present' ? 'Absent' : 'Present',
            };
            // console.log('Updated editAttendanceRecords:', newRecords);
            return newRecords;
        });
    };

    const filteredStudents = () => {
        if (!selectedClass || !students[selectedClass]) {
            return [];
        }
        let filtered = students[selectedClass];
        if (filterStudent) {
            filtered = filtered.filter(
                (student) =>
                    student?.fullName.toLowerCase().includes(filterStudent.toLowerCase()) ||
                    student?.registrationNumber.toLowerCase().includes(filterStudent.toLowerCase())
            );
        }
        return filtered;
    };

    const isModalSubmitDisabled = !attendanceDate;
    const isAttendanceSubmitDisabled = !isAttendanceMarking || !attendanceDate;
    const isEditSubmitDisabled = !isAttendanceMarking || !editDate;
    const isEditButtonDisabled = !previousAttendance || !previousAttendance[selectedClass] || previousAttendance[selectedClass].length === 0 || !filterDate;

    return (
        <>
            <div
                className="header d-flex align-items-center"
                style={{
                    minHeight: '600px',
                    backgroundImage: `url(${require('../../assets/img/theme/alRehman.jpg')})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center top',
                }}
            >
                <span className="mask bg-gradient-default opacity-8" />
                <Container className="d-flex align-items-center" fluid>
                    <Row>
                        <Col lg="7" md="10">
                            <h1 className="display-2 text-white">Student Attendance</h1>
                            <p className="text-white mt-0 mb-5">
                                Manage student attendance efficiently.
                            </p>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow">
                            <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">Attendance Records</h3>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Nav tabs>
                                        <NavItem>
                                            <NavLink
                                                className={activeTab === 'add' ? 'active' : ''}
                                                onClick={() => toggleTab('add')}
                                            >
                                                Add New Attendance
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                className={activeTab === 'view' ? 'active' : ''}
                                                onClick={() => toggleTab('view')}
                                            >
                                                View Previous Attendance
                                            </NavLink>
                                        </NavItem>
                                    </Nav>
                                    <FormGroup style={{ marginBottom: 0, width: '200px' }}>
                                        <Label>Select Class</Label>
                                        <Input
                                            type="select"
                                            value={selectedClass}
                                            onChange={handleClassChange}
                                        >
                                            <option value="">Select Class</option>
                                            {Object.keys(students).map((className) => (
                                                <option key={className} value={className}>
                                                    {className}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </div>
                                {selectedClass && (
                                    <div>
                                        {activeTab === 'add' && (
                                            <div className="mt-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <FormGroup style={{ marginBottom: 0 }}>
                                                        <Label>Filter by Student/Reg No</Label>
                                                        <Input
                                                            type="text"
                                                            placeholder="Search by Name/Reg No"
                                                            value={filterStudent}
                                                            onChange={handleFilterStudentChange}
                                                            innerRef={searchInputRef}
                                                        />
                                                    </FormGroup>
                                                    <div>
                                                        {!isAttendanceMarking && (
                                                            <Button color="primary" onClick={openModal}>
                                                                Mark New Attendance
                                                            </Button>
                                                        )}
                                                        {isAttendanceMarking && (
                                                            <Button
                                                                color="primary"
                                                                onClick={submitAttendance}
                                                                disabled={isAttendanceSubmitDisabled || loading}
                                                            >
                                                                {loading ? 'Submitting...' : 'Submit Attendance'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                                <Modal isOpen={modalOpen} toggle={closeModal}>
                                                    <ModalHeader toggle={closeModal}>
                                                        Mark Attendance
                                                    </ModalHeader>
                                                    <ModalBody>
                                                        <Form>
                                                            <FormGroup>
                                                                <Label>Select Date</Label>
                                                                <Input
                                                                    type="date"
                                                                    value={attendanceDate}
                                                                    onChange={handleAttendanceDateChange}
                                                                    max={formatDate(new Date())}
                                                                />
                                                            </FormGroup>
                                                        </Form>
                                                    </ModalBody>
                                                    <ModalFooter>
                                                        <Button
                                                            color="primary"
                                                            onClick={handleMarkAttendance}
                                                            disabled={isModalSubmitDisabled}
                                                        >
                                                            Submit
                                                        </Button>
                                                        <Button color="secondary" onClick={closeModal}>
                                                            Cancel
                                                        </Button>
                                                    </ModalFooter>
                                                </Modal>
                                                {isAttendanceMarking && (
                                                    <Table
                                                        className="align-items-center table-flush"
                                                        responsive
                                                    >
                                                        <thead className="thead-light">
                                                            <tr>
                                                                <th>Reg No</th>
                                                                <th>Name</th>
                                                                <th>Class Name</th>
                                                                <th style={{ textAlign: 'center' }}>Present</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredStudents().length > 0 ? (
                                                                filteredStudents().map((student) => {
                                                                    const existingAttendance = previousAttendance[selectedClass]?.find(att => att.studentId === student._id);
                                                                    const isFingerprint = existingAttendance?.attendance[0]?.remarks === '';
                                                                    // console.log(`Add table - Student ${student._id}: status=${existingAttendance?.attendance[0]?.status}, remarks=${existingAttendance?.attendance[0]?.remarks}, isFingerprint=${isFingerprint}`);
                                                                    return (
                                                                        <tr
                                                                            key={student?._id || '-'}
                                                                            style={student.leaveStatus === 'ON LEAVE' || isFingerprint ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                                                                        >
                                                                            <td>{student?.registrationNumber || '-'}</td>
                                                                            <td>{student?.fullName || '-'}</td>
                                                                            <td>{selectedClass}</td>
                                                                            <td style={{ textAlign: 'center' }}>
                                                                                <Input
                                                                                    type="checkbox"
                                                                                    checked={attendanceRecords[student._id] === 'Present'}
                                                                                    onChange={() => handleAttendanceChange(student._id)}
                                                                                    disabled={student.leaveStatus === 'ON LEAVE' || isFingerprint}
                                                                                />
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="4" className="text-center">
                                                                        No students found in this class.
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </Table>
                                                )}
                                            </div>
                                        )}
                                        {activeTab === 'view' && (
                                            <div className="mt-3">
                                                <Row className="mb-3">
                                                    <Col md="4" className="d-flex align-items-center">
                                                        <FormGroup style={{ marginBottom: 0 }}>
                                                            <Label>Filter by Date</Label>
                                                            <Input
                                                                type="date"
                                                                value={filterDate}
                                                                onChange={handleFilterDateChange}
                                                                max={formatDate(new Date())}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="4">
                                                        <FormGroup style={{ marginBottom: 0 }}>
                                                            <Label>Filter by Student/Reg No</Label>
                                                            <Input
                                                                type="text"
                                                                placeholder="Search by Name/Reg No"
                                                                value={filterStudent}
                                                                onChange={handleFilterStudentChange}
                                                                innerRef={searchInputRef}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                                <div className="d-flex justify-content-end">
                                                    <Button
                                                        color="info"
                                                        onClick={openEditModal}
                                                        disabled={isEditButtonDisabled || loading}
                                                    >
                                                        Edit Attendance
                                                    </Button>
                                                </div>
                                                {loading ? (
                                                    <p>Loading attendance data...</p>
                                                ) : previousAttendance && previousAttendance[selectedClass] && previousAttendance[selectedClass].length > 0 ? (
                                                    <Table
                                                        className="align-items-center table-flush"
                                                        responsive
                                                    >
                                                        <thead className="thead-light">
                                                            <tr>
                                                                <th>Reg No</th>
                                                                <th>Name</th>
                                                                <th>Class Name</th>
                                                                <th>Status</th>
                                                                <th>Check-In</th>
                                                                <th>Remarks</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {previousAttendance[selectedClass]
                                                                .filter(student =>
                                                                    !filterStudent ||
                                                                    student?.fullName.toLowerCase().includes(filterStudent.toLowerCase()) ||
                                                                    student?.registrationNumber.toLowerCase().includes(filterStudent.toLowerCase())
                                                                )
                                                                .map((student) => (
                                                                    <tr
                                                                        key={student?.studentId || '-'}
                                                                        style={student.leaveStatus === 'ON LEAVE' ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                                                                    >
                                                                        <td>{student?.registrationNumber || '-'}</td>
                                                                        <td>{student?.fullName || '-'}</td>
                                                                        <td>{selectedClass}</td>
                                                                        <td>{student.attendance[0].status || '-'}</td>
                                                                        <td>{formatTime(student.attendance[0].checkIn)}</td>
                                                                        <td>{student.attendance[0].remarks || '-'}</td>
                                                                    </tr>
                                                                ))}
                                                        </tbody>
                                                    </Table>
                                                ) : (
                                                    <p>No attendance records found for this date.</p>
                                                )}
                                                <Modal isOpen={editModalOpen} toggle={closeEditModal}>
                                                    <ModalHeader toggle={closeEditModal}>
                                                        Select Attendance Date
                                                    </ModalHeader>
                                                    <ModalBody>
                                                        <Form>
                                                            <FormGroup>
                                                                <Label>Select Date</Label>
                                                                <Input
                                                                    type="date"
                                                                    value={editDate}
                                                                    onChange={handleEditDateChange}
                                                                    max={formatDate(new Date())}
                                                                />
                                                            </FormGroup>
                                                        </Form>
                                                    </ModalBody>
                                                    <ModalFooter>
                                                        <Button
                                                            color="primary"
                                                            onClick={handleEditDateSubmit}
                                                            disabled={!editDate}
                                                        >
                                                            Submit
                                                        </Button>
                                                        <Button color="secondary" onClick={closeEditModal}>
                                                            Cancel
                                                        </Button>
                                                    </ModalFooter>
                                                </Modal>
                                                {isAttendanceMarking && (
                                                    <div className="mt-2">
                                                        {editAttendanceData.length === 0 ? (
                                                            <p>No attendance records found for this date.</p>
                                                        ) : (
                                                            <Table
                                                                className="align-items-center table-flush"
                                                                responsive
                                                            >
                                                                <thead className="thead-light">
                                                                    <tr>
                                                                        <th>Reg No</th>
                                                                        <th>Name</th>
                                                                        <th>Class Name</th>
                                                                        <th style={{ textAlign: 'center' }}>Present</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {filteredStudents().map((student) => {
                                                                        const attendance = editAttendanceData.find(att => att.studentId === student._id);
                                                                        const isPresent = attendance?.attendance[0]?.status === 'Present';
                                                                        const isFingerprint = attendance?.attendance[0]?.remarks === '';
                                                                        // console.log(`Edit table - Student ${student._id}: status=${attendance?.attendance[0]?.status}, remarks=${attendance?.attendance[0]?.remarks}, isFingerprint=${isFingerprint}, leaveStatus=${attendance?.leaveStatus}`);
                                                                        return (
                                                                            <tr
                                                                                key={student?._id || '-'}
                                                                                style={attendance?.leaveStatus === 'ON LEAVE' || isFingerprint ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                                                                            >
                                                                                <td>{student?.registrationNumber || '-'}</td>
                                                                                <td>{student?.fullName || '-'}</td>
                                                                                <td>{selectedClass}</td>
                                                                                <td style={{ textAlign: 'center' }}>
                                                                                    <Input
                                                                                        type="checkbox"
                                                                                        checked={editAttendanceRecords[student._id] === 'Present'}
                                                                                        onChange={() => handleEditAttendanceChange(student._id)}
                                                                                        disabled={attendance?.leaveStatus === 'ON LEAVE' || isFingerprint}
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </Table>
                                                        )}
                                                        <div className="d-flex justify-content-end mt-2">
                                                            <Button
                                                                color="primary"
                                                                onClick={submitEditAttendance}
                                                                disabled={isEditSubmitDisabled || loading}
                                                            >
                                                                {loading ? 'Updating...' : 'Update Attendance'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ToastContainer />
            <Footer />
        </>
    );
};

export default AddStudentAttendance;